import PropTypes from "prop-types";
import { Box, Button, Chip, Tooltip, useTheme } from "@mui/material";

// Per-field extraction confidence (Design Intent 1.3): high is unmarked, medium
// gets a subtle outline, low is called out explicitly and must be edited or
// acknowledged ("Looks right") before the line can be confirmed.
const FieldConfidence = ({
  level,
  isCleared,
  onAcknowledge,
  disabled,
  children
}) => {
  const { palette } = useTheme();

  if (level === "medium") {
    return (
      <Tooltip title="We're fairly sure about this value — worth a glance.">
        {/* An `outline` (not `border`) takes no layout space and draws
            outside a child's own box, so this cue shows up the same way
            whether the child is a bordered MUI input (recoloring its own
            notched outline would do nothing extra here, so this alone
            replaces that approach) or plain text like a Typography label
            -- the latter had no visual cue at all under the old
            notchedOutline-only approach, since that selector only matches
            a real MUI input. */}
        <Box
          data-testid="field-confidence-medium"
          sx={{
            display: "inline-block",
            outline: `1px solid ${palette.warning.light}`,
            outlineOffset: 2,
            borderRadius: 1
          }}
        >
          {children}
        </Box>
      </Tooltip>
    );
  }

  if (level !== "low" || isCleared) {
    return <>{children}</>;
  }

  return (
    <Box
      data-testid="field-confidence-low"
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-start",
        gap: 0.5,
        border: `1px solid ${palette.error.main}`,
        borderRadius: 1,
        p: 0.5
      }}
    >
      {children}
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
        <Chip
          size="small"
          color="error"
          variant="outlined"
          label="Low confidence"
        />
        <Button size="small" disabled={disabled} onClick={onAcknowledge}>
          Looks right
        </Button>
      </Box>
    </Box>
  );
};

FieldConfidence.propTypes = {
  level: PropTypes.oneOf(["high", "medium", "low"]).isRequired,
  isCleared: PropTypes.bool,
  onAcknowledge: PropTypes.func,
  disabled: PropTypes.bool,
  children: PropTypes.node.isRequired
};

export default FieldConfidence;
